import { useState } from 'react';
import { SkeletonFilas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { ESTADOS, useResumen, useSolicitudes } from './api';
import { DetalleDeSolicitud } from './DetalleDeSolicitud';
import { FilaDeSolicitud } from './piezas';

/**
 * Bandeja — los radicados de todo el mundo, con su resumen por estado.
 *
 * El resumen y la lista se piden por separado. El vanilla los pedía con un
 * `Promise.all` dentro del mismo `try`, así que un fallo del resumen —que es un
 * recuento— se llevaba por delante la lista, que es el trabajo.
 */
export function Bandeja({ activa }: { activa: boolean }) {
  const resumen = useResumen(activa);
  const consulta = useSolicitudes(activa);
  const [abierta, setAbierta] = useState<number | null>(null);

  const filas = consulta.data ?? [];
  const cuentas = resumen.data?.counts ?? {};

  return (
    <>
      {/* Si el resumen falla, la bandeja se sigue viendo: es un recuento. */}
      {!resumen.isError && (
        <dl className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
          {ESTADOS.map(([clave, etiqueta]) => (
            <div key={clave} className="rounded-sm border border-line bg-surface p-2 text-center">
              <dt className="sr-only">{etiqueta}</dt>
              <dd>
                <strong className="block text-6 font-extrabold leading-none tabular-nums text-brand-ink">
                  {resumen.isPending ? '—' : (cuentas[clave] ?? 0)}
                </strong>
                <span className="mt-1 block text-1 text-muted" aria-hidden="true">{etiqueta}</span>
              </dd>
            </div>
          ))}
        </dl>
      )}

      {consulta.isPending ? (
        <SkeletonFilas filas={5} columnas={3} />
      ) : consulta.isError ? (
        <EstadoError
          err={consulta.error}
          que="la bandeja de documentos"
          onReintentar={() => void consulta.refetch()}
        />
      ) : !filas.length ? (
        <EstadoVacio
          icono="bi-inbox"
          titulo="Sin solicitudes"
          descripcion="Todavía no hay radicados de colaboradores."
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {filas.map((s) => (
            <li key={s.id}>
              <FilaDeSolicitud solicitud={s} conAutor onAbrir={() => setAbierta(s.id)} />
            </li>
          ))}
        </ul>
      )}

      {abierta != null && (
        <DetalleDeSolicitud id={abierta} esGestion onCerrar={() => setAbierta(null)} />
      )}
    </>
  );
}
